/**
 * Wave 3 backbone: the player's ACTIVE modular loadout, persisted so a fitted
 * ship survives reloads. The pure model lives in sim-core (applyLoadout →
 * EffectiveShip); this is a tiny, graceful client wrapper around the dedicated
 * localStorage key 'blackmarble.loadout.v1' (same versioned pattern as
 * settings/store.ts).
 *
 * Graceful by contract: missing, corrupt, or version-mismatched storage always
 * yields a FRESH copy of DEFAULT_SKIFF_LOADOUT (the Pallbearer's default fit) —
 * it never throws. serialize/deserialize are pure + exported for tests.
 */
import { DEFAULT_SKIFF_LOADOUT, type Loadout } from '@blackmarble/sim-core';

const LOADOUT_KEY = 'blackmarble.loadout.v1';
const LOADOUT_VERSION = 1;

interface StoredLoadout {
  version: number;
  loadout: Loadout;
}

type Listener = (loadout: Loadout) => void;

/** Deep copy so callers can never mutate the shared default / active reference. */
const clone = (loadout: Loadout): Loadout => JSON.parse(JSON.stringify(loadout)) as Loadout;

/** A fresh, independent copy of the Pallbearer's default fit. */
const defaultLoadout = (): Loadout => clone(DEFAULT_SKIFF_LOADOUT);

/** Structural guard — a Loadout is a non-empty hardpoints[] + a plain fitted map. */
const isLoadout = (value: unknown): value is Loadout => {
  if (typeof value !== 'object' || value === null) return false;
  const l = value as Partial<Loadout>;
  return (
    Array.isArray(l.hardpoints) &&
    l.hardpoints.length > 0 &&
    typeof l.fitted === 'object' &&
    l.fitted !== null &&
    !Array.isArray(l.fitted)
  );
};

/** Serialize a loadout into a versioned JSON envelope. Pure. */
export const serializeLoadout = (loadout: Loadout): string =>
  JSON.stringify({ version: LOADOUT_VERSION, loadout } satisfies StoredLoadout);

/**
 * Parse a stored loadout envelope. Returns a fresh DEFAULT_SKIFF_LOADOUT on
 * null / corrupt / version-mismatch / shape-mismatch — never throws. Pure.
 */
export const deserializeLoadout = (raw: string | null): Loadout => {
  if (!raw) return defaultLoadout();
  try {
    const parsed = JSON.parse(raw) as Partial<StoredLoadout>;
    if (parsed.version === LOADOUT_VERSION && isLoadout(parsed.loadout)) {
      return parsed.loadout;
    }
  } catch {
    /* corrupt JSON — fall through to the default fit */
  }
  return defaultLoadout();
};

/** Read the persisted envelope; any storage error degrades to the default. */
const readStored = (): Loadout => {
  try {
    return deserializeLoadout(localStorage.getItem(LOADOUT_KEY));
  } catch {
    return defaultLoadout();
  }
};

class LoadoutStore {
  private active = readStored();
  private listeners = new Set<Listener>();

  constructor() {
    this.persist(); // seed the key on first run (best effort)
  }

  /** The active loadout (DEFAULT_SKIFF_LOADOUT on a fresh / corrupt store). */
  getActive(): Loadout {
    return this.active;
  }

  /** Replace + persist the active loadout, then notify subscribers. */
  setActive(loadout: Loadout): void {
    this.active = loadout;
    this.persist();
    this.emit();
  }

  /** Restore (and persist) the Pallbearer's default fit. */
  reset(): void {
    this.active = defaultLoadout();
    this.persist();
    this.emit();
  }

  /** Subscribe to active-loadout changes; returns an unsubscribe fn. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.active);
  }

  private persist(): void {
    try {
      localStorage.setItem(LOADOUT_KEY, serializeLoadout(this.active));
    } catch {
      /* storage is best-effort — play continues without persistence */
    }
  }
}

export const loadoutStore = new LoadoutStore();
export const getActive = (): Loadout => loadoutStore.getActive();
export const setActive = (loadout: Loadout): void => loadoutStore.setActive(loadout);
export const reset = (): void => loadoutStore.reset();
